import React, { useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import { X, ShieldAlert, RotateCw } from "lucide-react";
import { useReducedMotion } from "../../hooks/useReducedMotion.ts";
import { formatDateTimeIST } from "../../utils/dateTime.ts";
import { Skeleton } from "../common/Skeleton.tsx";
import type { RequestRecord } from "../../types/telemetry.ts";

interface RequestDetailPanelProps {
  isOpen: boolean;
  requestId: string | null;
  request: RequestRecord | null;
  isLoading: boolean;
  error: Error | null;
  onRetry?: () => void;
  onClose: () => void;
  isSyntheticMode?: boolean;
}

/** Focusable element selectors for focus trap */
const FOCUSABLE_SELECTORS =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const RequestDetailPanel: React.FC<RequestDetailPanelProps> = ({
  isOpen,
  requestId,
  request,
  isLoading,
  error,
  onRetry,
  onClose,
  isSyntheticMode = false,
}) => {
  const prefersReducedMotion = useReducedMotion();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Focus the close button when panel opens
  useEffect(() => {
    if (isOpen) {
      // Use rAF to ensure motion animation doesn't fight focus
      requestAnimationFrame(() => {
        closeButtonRef.current?.focus();
      });
    }
  }, [isOpen]);

  // Focus trap: keep Tab/Shift+Tab inside the panel; Escape closes
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== "Tab") return;

      const panel = panelRef.current;
      if (!panel) return;

      const focusableEls = Array.from(
        panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)
      ).filter((el) => !el.hasAttribute("disabled") && el.offsetParent !== null);

      if (focusableEls.length === 0) return;

      const first = focusableEls[0];
      const last = focusableEls[focusableEls.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === first) {
          event.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    },
    [onClose]
  );

  const slideTransition = prefersReducedMotion
    ? { duration: 0 }
    : { duration: 0.15, ease: "easeOut" };

  // Determine honest upstream latency explanation
  const renderUpstreamLatency = () => {
    if (!request) return "—";
    if (request.upstream_latency_ms !== null) {
      return `${request.upstream_latency_ms} ms`;
    }
    if (request.gateway_outcome === "BLOCKED") {
      return "— (Policy block; request not dispatched upstream)";
    }
    if (request.gateway_outcome === "REJECTED") {
      return "— (Ingress rejection; request not dispatched upstream)";
    }
    if (request.gateway_outcome === "GATEWAY_ERROR") {
      return "— (Inspection error; request not dispatched upstream)";
    }
    if (request.gateway_outcome === "LEGACY_UNVERIFIED") {
      return "— (Historical record; upstream latency not captured)";
    }
    return "— (Not available)";
  };

  const getStatusLabel = (code: number) => {
    const labels: Record<number, string> = {
      200: "OK",
      201: "Created",
      400: "Bad Request",
      403: "Forbidden",
      404: "Not Found",
      405: "Method Not Allowed",
      413: "Payload Too Large",
      500: "Internal Server Error",
      502: "Bad Gateway",
      504: "Gateway Timeout",
    };
    return labels[code] || "Status";
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.1 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-40"
            aria-hidden="true"
          />

          {/* Side Panel */}
          <motion.div
            ref={panelRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-label="Request details"
            aria-labelledby="detail-panel-title"
            initial={prefersReducedMotion ? { opacity: 0 } : { x: "100%" }}
            animate={prefersReducedMotion ? { opacity: 1 } : { x: 0 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { x: "100%" }}
            transition={slideTransition}
            onKeyDown={handleKeyDown}
            className="fixed top-0 right-0 h-full w-full sm:w-[480px] bg-panel border-l border-border z-50 flex flex-col focus:outline-none shadow-2xl"
          >
            {/* Header */}
            <div className="h-12 border-b border-border px-5 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                {request ? (
                  <>
                    <span className="text-[11px] font-mono font-medium px-1.5 py-0.5 rounded bg-panel-subtle border border-border text-foreground">
                      {request.method}
                    </span>
                    <h3
                      id="detail-panel-title"
                      className="text-sm font-medium text-foreground truncate max-w-[240px]"
                    >
                      {request.raw_path}
                    </h3>
                  </>
                ) : (
                  <>
                    <span className="text-[11px] font-mono font-medium px-1.5 py-0.5 rounded bg-panel-subtle border border-border text-foreground-muted">
                      UUID
                    </span>
                    <h3
                      id="detail-panel-title"
                      className="text-sm font-mono text-foreground truncate max-w-[240px]"
                    >
                      {requestId ? `${requestId.slice(0, 8)}...` : "Details"}
                    </h3>
                  </>
                )}
                <span
                  className={`text-[11px] px-1.5 py-0.5 rounded font-medium border ${
                    isSyntheticMode
                      ? "bg-amber-500/10 text-amber-400 border-amber-500/30"
                      : "bg-brand-subtle text-brand border-brand-border"
                  }`}
                >
                  {isSyntheticMode ? "Synthetic" : "Live"}
                </span>
              </div>
              <button
                ref={closeButtonRef}
                type="button"
                onClick={onClose}
                aria-label="Close details panel"
                className="p-1 rounded text-foreground-muted hover:text-foreground hover:bg-panel-hover transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content area: loading, error, or data */}
            {isLoading ? (
              <div
                data-testid="request-detail-loading"
                role="region"
                aria-busy="true"
                aria-label="Loading request details"
                className="p-6 space-y-6 overflow-y-auto flex-grow text-sm"
              >
                <span className="sr-only">Loading request inspection details</span>

                {/* Group 1 Skeleton: General */}
                <div className="space-y-3">
                  <Skeleton className="h-3 w-16" />
                  <div className="space-y-2 divide-y divide-border-subtle">
                    <div className="flex justify-between py-1"><Skeleton className="h-3.5 w-24" /><Skeleton className="h-3.5 w-36" /></div>
                    <div className="flex justify-between py-1 pt-2"><Skeleton className="h-3.5 w-16" /><Skeleton className="h-3.5 w-12" /></div>
                    <div className="flex justify-between py-1 pt-2"><Skeleton className="h-3.5 w-12" /><Skeleton className="h-3.5 w-44" /></div>
                    <div className="flex justify-between py-1 pt-2"><Skeleton className="h-3.5 w-16" /><Skeleton className="h-3.5 w-24" /></div>
                    <div className="flex justify-between py-1 pt-2"><Skeleton className="h-3.5 w-28" /><Skeleton className="h-3.5 w-20" /></div>
                    <div className="flex justify-between py-1 pt-2"><Skeleton className="h-3.5 w-32" /><Skeleton className="h-3.5 w-48" /></div>
                  </div>
                </div>

                {/* Group 2 Skeleton: Policy Decision & Audit */}
                <div className="space-y-3 pt-2">
                  <Skeleton className="h-3 w-36" />
                  <div className="space-y-2 divide-y divide-border-subtle">
                    <div className="flex justify-between py-1"><Skeleton className="h-3.5 w-32" /><Skeleton className="h-3.5 w-20" /></div>
                    <div className="flex justify-between py-1 pt-2"><Skeleton className="h-3.5 w-32" /><Skeleton className="h-5 w-16 rounded" /></div>
                    <div className="flex justify-between py-1 pt-2"><Skeleton className="h-3.5 w-28" /><Skeleton className="h-5 w-16 rounded" /></div>
                    <div className="flex justify-between py-1 pt-2"><Skeleton className="h-3.5 w-24" /><Skeleton className="h-3.5 w-20" /></div>
                    <div className="flex justify-between py-1 pt-2"><Skeleton className="h-3.5 w-24" /><Skeleton className="h-3.5 w-24" /></div>
                    <div className="flex justify-between py-1 pt-2"><Skeleton className="h-3.5 w-36" /><Skeleton className="h-3.5 w-28" /></div>
                  </div>
                </div>

                {/* Group 3 Skeleton: WAF Inspection */}
                <div className="space-y-3 pt-2">
                  <Skeleton className="h-3 w-28" />
                  <div className="space-y-2 divide-y divide-border-subtle">
                    <div className="flex justify-between py-1"><Skeleton className="h-3.5 w-28" /><Skeleton className="h-3.5 w-20" /></div>
                    <div className="flex justify-between py-1 pt-2"><Skeleton className="h-3.5 w-24" /><Skeleton className="h-3.5 w-10" /></div>
                    <div className="flex justify-between py-1 pt-2"><Skeleton className="h-3.5 w-24" /><Skeleton className="h-3.5 w-10" /></div>
                  </div>
                </div>

                {/* Group 4 Skeleton: AI Classification */}
                <div className="space-y-3 pt-2">
                  <Skeleton className="h-3 w-32" />
                  <div className="space-y-2 divide-y divide-border-subtle">
                    <div className="flex justify-between py-1"><Skeleton className="h-3.5 w-20" /><Skeleton className="h-3.5 w-16" /></div>
                    <div className="flex justify-between py-1 pt-2"><Skeleton className="h-3.5 w-28" /><Skeleton className="h-3.5 w-20" /></div>
                    <div className="flex justify-between py-1 pt-2"><Skeleton className="h-3.5 w-28" /><Skeleton className="h-3.5 w-24" /></div>
                  </div>
                </div>

                {/* Group 5 Skeleton: Latencies */}
                <div className="space-y-3 pt-2">
                  <Skeleton className="h-3 w-32" />
                  <div className="space-y-2 divide-y divide-border-subtle">
                    <div className="flex justify-between py-1"><Skeleton className="h-3.5 w-36" /><Skeleton className="h-3.5 w-16" /></div>
                    <div className="flex justify-between py-1 pt-2"><Skeleton className="h-3.5 w-32" /><Skeleton className="h-3.5 w-16" /></div>
                    <div className="flex justify-between py-1 pt-2"><Skeleton className="h-3.5 w-32" /><Skeleton className="h-3.5 w-16" /></div>
                  </div>
                </div>
              </div>
            ) : error ? (
              <div
                data-testid="request-detail-error"
                className="p-8 flex flex-col items-center justify-center flex-grow text-center space-y-3"
              >
                <ShieldAlert className="w-8 h-8 text-verdict-block mx-auto" />
                <h4 className="text-sm font-medium text-foreground">
                  {(error as any)?.status === 404
                    ? "Request record not found"
                    : "Unable to load request details"}
                </h4>
                <p className="text-xs text-foreground-muted max-w-xs">
                  {(error as any)?.status === 404
                    ? "The requested inspection record was not found or has expired from the telemetry log."
                    : error.message || "An unexpected error occurred while communicating with the management API."}
                </p>
                {onRetry && (
                  <button
                    type="button"
                    onClick={onRetry}
                    className="px-3 py-1.5 rounded-md bg-brand text-white text-xs font-medium hover:bg-brand-hover transition-colors inline-flex items-center gap-1.5"
                  >
                    <RotateCw className="w-3.5 h-3.5" />
                    Retry
                  </button>
                )}
              </div>
            ) : !request ? (
              <div className="p-8 flex flex-col items-center justify-center flex-grow text-center text-foreground-muted text-xs">
                No request details to display.
              </div>
            ) : (
              /* Scrollable Content */
              <div className="p-6 space-y-6 overflow-y-auto flex-grow text-sm">

              {/* Group 1: General */}
              <div className="space-y-3">
                <h4 className="text-xs font-medium uppercase tracking-wider text-foreground-muted">
                  General
                </h4>
                <div className="space-y-2 text-sm divide-y divide-border-subtle">
                  <div className="flex justify-between py-1">
                    <span className="text-foreground-muted">Timestamp (IST)</span>
                    <span className="font-mono text-foreground text-xs">
                      {formatDateTimeIST(request.timestamp, { preserveSubseconds: true })}
                    </span>
                  </div>
                  <div className="flex justify-between py-1 pt-2">
                    <span className="text-foreground-muted">Raw UTC</span>
                    <span className="font-mono text-foreground-dim text-xs">
                      {request.timestamp || "—"}
                    </span>
                  </div>
                  <div className="flex justify-between py-1 pt-2">
                    <span className="text-foreground-muted">Method</span>
                    <span className="font-mono font-medium text-foreground">{request.method}</span>
                  </div>
                  <div className="flex justify-between py-1 pt-2">
                    <span className="text-foreground-muted">Path</span>
                    <span className="font-mono text-foreground text-xs break-all text-right max-w-[280px]">
                      {request.raw_path}
                      {request.query_string ? `?${request.query_string}` : ""}
                    </span>
                  </div>
                  <div className="flex justify-between py-1 pt-2">
                    <span className="text-foreground-muted">Client IP</span>
                    <span className="font-mono text-foreground text-xs">{request.client_ip}</span>
                  </div>
                  <div className="flex justify-between py-1 pt-2">
                    <span className="text-foreground-muted">Client Request ID</span>
                    <span className="font-mono text-xs text-foreground">
                      {request.client_request_id || "—"}
                    </span>
                  </div>
                  <div className="flex justify-between py-1 pt-2">
                    <span className="text-foreground-muted">Server Request UUID</span>
                    <span
                      className="font-mono text-xs text-foreground-muted hover:text-brand-hover select-all cursor-pointer transition-colors"
                      title="Select UUID"
                    >
                      {request.id}
                    </span>
                  </div>
                </div>
              </div>

              {/* Group 2: Policy Decision & Audit */}
              <div className="space-y-3 pt-2">
                <h4 className="text-xs font-medium uppercase tracking-wider text-foreground-muted">
                  Policy Decision &amp; Audit
                </h4>
                <div className="space-y-2 text-sm divide-y divide-border-subtle">
                  <div className="flex justify-between py-1">
                    <span className="text-foreground-muted">Gateway Policy Mode</span>
                    <span className="font-mono text-foreground text-xs">
                      {request.policy_mode ? request.policy_mode : "Unavailable"}
                    </span>
                  </div>
                  <div className="flex justify-between py-1 pt-2">
                    <span className="text-foreground-muted">Original CRS Verdict</span>
                    <span
                      className={`text-xs px-2 py-0.5 rounded font-medium ${
                        request.crs_policy_decision === "ALLOW"
                          ? "bg-verdict-allow-bg text-verdict-allow border border-verdict-allow/30"
                          : request.crs_policy_decision === "BLOCK"
                          ? "bg-verdict-block-bg text-verdict-block border border-verdict-block/30"
                          : request.crs_policy_decision === "FLAG"
                          ? "bg-verdict-flag-bg text-verdict-flag border border-verdict-flag/30"
                          : "bg-verdict-neutral-bg text-verdict-neutral border border-border"
                      }`}
                    >
                      {request.crs_policy_decision ?? "Unavailable"}
                    </span>
                  </div>
                  <div className="flex justify-between py-1 pt-2">
                    <span className="text-foreground-muted">Final Policy Verdict</span>
                    <span
                      className={`text-xs px-2 py-0.5 rounded font-medium ${
                        request.policy_decision === "ALLOW"
                          ? "bg-verdict-allow-bg text-verdict-allow border border-verdict-allow/30"
                          : request.policy_decision === "BLOCK"
                          ? "bg-verdict-block-bg text-verdict-block border border-verdict-block/30"
                          : request.policy_decision === "FLAG"
                          ? "bg-verdict-flag-bg text-verdict-flag border border-verdict-flag/30"
                          : "bg-verdict-neutral-bg text-verdict-neutral border border-border"
                      }`}
                    >
                      {request.policy_decision ?? "Unavailable"}
                    </span>
                  </div>
                  <div className="flex justify-between py-1 pt-2">
                    <span className="text-foreground-muted">Decision Source</span>
                    <span className="font-mono text-xs text-foreground font-medium">
                      {request.decision_source ?? "Unavailable"}
                    </span>
                  </div>
                  <div className="flex justify-between py-1 pt-2">
                    <span className="text-foreground-muted">Decision Reason</span>
                    <span className="font-mono text-xs text-foreground font-medium">
                      {request.decision_reason ?? "Unavailable"}
                    </span>
                  </div>
                  <div className="flex justify-between py-1 pt-2">
                    <span className="text-foreground-muted">Recorded AI Mode</span>
                    <span className="font-mono text-xs text-foreground">
                      {request.ai_policy_mode ?? "Unavailable"}
                    </span>
                  </div>
                  <div className="flex justify-between py-1 pt-2">
                    <span className="text-foreground-muted">AI Thresholds</span>
                    <span className="font-mono text-xs text-foreground">
                      {request.ai_flag_threshold !== null && request.ai_flag_threshold !== undefined && request.ai_block_threshold !== null && request.ai_block_threshold !== undefined
                        ? `Flag: ${request.ai_flag_threshold.toFixed(2)} / Block: ${request.ai_block_threshold.toFixed(2)}`
                        : "Unavailable"}
                    </span>
                  </div>
                  <div className="flex justify-between py-1 pt-2">
                    <span className="text-foreground-muted">Gateway Delivery Outcome</span>
                    <span className="font-mono text-xs text-foreground font-medium">
                      {request.gateway_outcome}
                    </span>
                  </div>
                  <div className="flex justify-between py-1 pt-2">
                    <span className="text-foreground-muted">Gateway HTTP Response</span>
                    <span className="font-mono text-foreground">
                      {request.response_status} {getStatusLabel(request.response_status)}
                    </span>
                  </div>
                  <div className="flex justify-between py-1 pt-2">
                    <span className="text-foreground-muted">Upstream Status</span>
                    <span className="font-mono text-foreground">
                      {request.upstream_status !== null
                        ? `${request.upstream_status} ${getStatusLabel(request.upstream_status)}`
                        : "—"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Group 3: Inspection Details */}
              <div className="space-y-3 pt-2">
                <h4 className="text-xs font-medium uppercase tracking-wider text-foreground-muted">
                  WAF Inspection
                </h4>
                <div className="space-y-2 text-sm divide-y divide-border-subtle">
                  <div className="flex justify-between py-1">
                    <span className="text-foreground-muted">Inspection Status</span>
                    <span className="font-mono text-foreground text-xs font-medium">
                      {request.inspection_status}
                    </span>
                  </div>
                  <div className="flex justify-between py-1 pt-2">
                    <span className="text-foreground-muted">Blocking Score</span>
                    <span className="font-mono text-foreground">
                      {request.blocking_score !== null ? request.blocking_score : "—"}
                    </span>
                  </div>
                  <div className="flex justify-between py-1 pt-2">
                    <span className="text-foreground-muted">Detection Score</span>
                    <span className="font-mono text-foreground">
                      {request.detection_score !== null ? request.detection_score : "—"}
                    </span>
                  </div>
                  {request.error_detail && (
                    <div className="py-1 pt-2 space-y-1">
                      <span className="text-foreground-muted text-xs">Diagnostic Reason</span>
                      <p className="font-mono text-xs text-verdict-flag bg-canvas p-2 rounded border border-border">
                        {request.error_detail}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Group 4: AI Classification */}
              <div className="space-y-3 pt-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-medium uppercase tracking-wider text-foreground-muted">
                    AI Classification
                  </h4>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-mono font-medium px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/30">
                      {request.ai_policy_mode ?? "SHADOW"}
                    </span>
                    {isSyntheticMode && (
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/30">
                        Fixture
                      </span>
                    )}
                  </div>
                </div>

                <div className="text-[11px] text-foreground-muted bg-panel-subtle px-2.5 py-1.5 rounded border border-border-subtle flex items-center justify-between">
                  <span>
                    {request.ai_policy_mode === "HYBRID"
                      ? request.policy_mode === "MONITORING"
                        ? "Observes; monitoring mode suppresses AI escalation."
                        : request.policy_mode === "FLAGGING"
                        ? "May escalate to flag within recorded thresholds; blocking disabled."
                        : "May escalate within the recorded gateway mode and thresholds."
                      : "Observes; does not control blocking."}
                  </span>
                  {request.ai_policy_mode && (
                    <span className="font-mono text-[10px] text-foreground-dim uppercase">
                      mode: {request.ai_policy_mode}
                    </span>
                  )}
                </div>

                <div className="space-y-2 text-sm divide-y divide-border-subtle">
                  {/* Status row */}
                  <div className="flex justify-between py-1">
                    <span className="text-foreground-muted">AI Status</span>
                    <span className="font-mono text-xs text-foreground font-medium">
                      {request.ai_status ?? "Not evaluated"}
                    </span>
                  </div>

                  {/* Historical NULL record handling */}
                  {(request.ai_status === null || request.ai_status === undefined) && (
                    <div className="py-2 text-xs text-foreground-dim">
                      Historical record processed without AI inference evaluation.
                    </div>
                  )}

                  {/* Honest non-OK results: no fake probability bars */}
                  {request.ai_status !== null &&
                    request.ai_status !== undefined &&
                    request.ai_status !== "OK" && (
                      <>
                        <div className="flex justify-between py-1 pt-2">
                          <span className="text-foreground-muted">Prediction</span>
                          <span className="text-xs text-foreground-muted italic">
                            {request.ai_status === "UNRELIABLE_REDACTION"
                              ? "Omitted (Unreliable redaction tags detected in payload)"
                              : request.ai_status.startsWith("SKIPPED_")
                              ? `Bypassed (${request.ai_status.replace("SKIPPED_", "").toLowerCase()} filter)`
                              : request.ai_status === "TIMEOUT"
                              ? "Omitted (Inference deadline exceeded 250 ms)"
                              : request.ai_status === "OVERLOADED"
                              ? "Omitted (Inference service busy / overloaded)"
                              : request.ai_status === "NOT_READY"
                              ? "Omitted (Inference service initializing / warming up)"
                              : "Omitted (Inference service unavailable)"}
                          </span>
                        </div>
                        {request.ai_model_identity && (
                          <div className="flex justify-between py-1 pt-2">
                            <span className="text-foreground-muted">Model Identity</span>
                            <span className="font-mono text-xs text-foreground">
                              {request.ai_model_identity}
                            </span>
                          </div>
                        )}
                        {request.ai_latency_ms !== null && request.ai_latency_ms !== undefined && (
                          <div className="flex justify-between py-1 pt-2">
                            <span className="text-foreground-muted">AI Round-trip Latency</span>
                            <span className="font-mono text-xs text-foreground">
                              {request.ai_latency_ms} ms
                            </span>
                          </div>
                        )}
                        {request.ai_preprocessor_truncated !== null &&
                          request.ai_preprocessor_truncated !== undefined && (
                            <div className="flex justify-between py-1 pt-2">
                              <span className="text-foreground-muted">Preprocessor Truncated</span>
                              <span className="font-mono text-xs text-foreground">
                                {request.ai_preprocessor_truncated ? "Yes" : "No"}
                              </span>
                            </div>
                          )}
                        {request.ai_token_truncated !== null &&
                          request.ai_token_truncated !== undefined && (
                            <div className="flex justify-between py-1 pt-2">
                              <span className="text-foreground-muted">Tokenizer Truncated</span>
                              <span className="font-mono text-xs text-foreground">
                                {request.ai_token_truncated ? "Yes" : "No"}
                              </span>
                            </div>
                          )}
                      </>
                    )}

                  {/* Valid OK results: show category, uncalibrated probability, and four class probabilities */}
                  {request.ai_status === "OK" && (
                    <>
                      <div className="pt-2 pb-1">
                        <span className="text-[10px] uppercase font-mono tracking-wider text-foreground-dim">
                          Model Output (Evaluated Separately from Enforcement)
                        </span>
                      </div>
                      <div className="flex justify-between py-1 pt-2">
                        <span className="text-foreground-muted">Predicted Category</span>
                        <span
                          className={`text-xs px-2 py-0.5 rounded font-mono font-medium ${
                            request.ai_predicted_category === "BENIGN"
                              ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30"
                              : "bg-amber-500/10 text-amber-400 border border-amber-500/30"
                          }`}
                        >
                          {request.ai_predicted_category ?? "—"}
                        </span>
                      </div>

                      <div className="flex justify-between py-1 pt-2">
                        <span className="text-foreground-muted">Model Probability</span>
                        <span className="font-mono text-xs text-foreground">
                          {request.ai_model_probability !== null &&
                          request.ai_model_probability !== undefined
                            ? `${(request.ai_model_probability * 100).toFixed(2)}% (${request.ai_model_probability.toFixed(4)})`
                            : "—"}{" "}
                          <span className="text-[10px] text-foreground-muted font-sans">
                            (uncalibrated)
                          </span>
                        </span>
                      </div>

                      {/* Four Class Probabilities */}
                      {request.ai_class_probabilities && (
                        <div className="py-2 space-y-1.5">
                          <span className="text-xs text-foreground-muted">
                            Class Probabilities
                          </span>
                          <div className="space-y-1 bg-canvas p-2.5 rounded border border-border">
                            {(["BENIGN", "SQLI", "XSS", "PATH_TRAVERSAL"] as const).map(
                              (cls) => {
                                const prob = request.ai_class_probabilities?.[cls] ?? 0.0;
                                const pct = (prob * 100).toFixed(1);
                                const isTop = cls === request.ai_predicted_category;
                                return (
                                  <div key={cls} className="space-y-0.5">
                                    <div className="flex justify-between text-xs font-mono">
                                      <span
                                        className={
                                          isTop
                                            ? "text-foreground font-medium"
                                            : "text-foreground-muted"
                                        }
                                      >
                                        {cls}
                                      </span>
                                      <span
                                        className={
                                          isTop
                                            ? "text-foreground font-medium"
                                            : "text-foreground-muted"
                                        }
                                      >
                                        {pct}%{" "}
                                        <span className="text-[10px] text-foreground-dim">
                                          ({prob.toFixed(4)})
                                        </span>
                                      </span>
                                    </div>
                                    <div className="h-1 w-full bg-panel-subtle rounded overflow-hidden">
                                      <div
                                        className={`h-full ${
                                          isTop
                                            ? cls === "BENIGN"
                                              ? "bg-emerald-500"
                                              : "bg-amber-500"
                                            : "bg-foreground-dim/40"
                                        }`}
                                        style={{ width: `${Math.min(100, Math.max(0, prob * 100))}%` }}
                                      />
                                    </div>
                                  </div>
                                );
                              }
                            )}
                          </div>
                        </div>
                      )}

                      {/* Provenance & Truncation details */}
                      {request.ai_model_identity && (
                        <div className="flex justify-between py-1 pt-2">
                          <span className="text-foreground-muted">Model Identity</span>
                          <span className="font-mono text-xs text-foreground">
                            {request.ai_model_identity}
                          </span>
                        </div>
                      )}

                      <div className="flex justify-between py-1 pt-2">
                        <span className="text-foreground-muted">Preprocessor Truncated</span>
                        <span className="font-mono text-xs text-foreground">
                          {request.ai_preprocessor_truncated ? "Yes" : "No"}
                        </span>
                      </div>

                      <div className="flex justify-between py-1 pt-2">
                        <span className="text-foreground-muted">Tokenizer Truncated</span>
                        <span className="font-mono text-xs text-foreground">
                          {request.ai_token_truncated ? "Yes" : "No"}
                        </span>
                      </div>

                      <div className="flex justify-between py-1 pt-2">
                        <span className="text-foreground-muted">AI Round-trip Latency</span>
                        <span className="font-mono text-xs text-foreground">
                          {request.ai_latency_ms !== null && request.ai_latency_ms !== undefined
                            ? `${request.ai_latency_ms} ms`
                            : "—"}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Group 5: Latencies */}
              <div className="space-y-3 pt-2">
                <h4 className="text-xs font-medium uppercase tracking-wider text-foreground-muted">
                  Latency Breakdown
                </h4>
                <div className="space-y-2 text-sm divide-y divide-border-subtle">
                  <div className="flex justify-between py-1">
                    <span className="text-foreground-muted">Total Transit Duration</span>
                    <span className="font-mono text-foreground">{request.latency_ms} ms</span>
                  </div>
                  <div className="flex justify-between py-1 pt-2">
                    <span className="text-foreground-muted">CRS Inspector Probe</span>
                    <span className="font-mono text-foreground">
                      {request.inspection_latency_ms !== null
                        ? `${request.inspection_latency_ms} ms`
                        : "— (Not run)"}
                    </span>
                  </div>
                  <div className="flex justify-between py-1 pt-2">
                    <span className="text-foreground-muted">AI Shadow Inference</span>
                    <span className="font-mono text-foreground">
                      {request.ai_latency_ms !== null && request.ai_latency_ms !== undefined
                        ? `${request.ai_latency_ms} ms`
                        : "— (Not evaluated)"}
                    </span>
                  </div>
                  <div className="flex justify-between py-1 pt-2">
                    <span className="text-foreground-muted">Time to Upstream Headers</span>
                    <span className="font-mono text-foreground text-xs">
                      {renderUpstreamLatency()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Group 5: Redacted Headers */}
              <div className="space-y-3 pt-2">
                <h4 className="text-xs font-medium uppercase tracking-wider text-foreground-muted">
                  Redacted Ingress Headers
                </h4>
                <pre className="p-3 rounded-md bg-canvas border border-border text-xs font-mono text-foreground-muted overflow-x-auto">
                  {JSON.stringify(request.headers, null, 2)}
                </pre>
              </div>

              {/* Group 6: Privacy Statement */}
              <div className="pt-2 text-xs text-foreground-dim border-t border-border-subtle">
                Raw request bodies and internal inspector payloads are omitted from persistent storage by design.
              </div>
            </div>
          )}

            {/* Footer */}
            <div className="h-12 border-t border-border px-5 flex items-center justify-between shrink-0 text-xs text-foreground-muted">
              <span>Press Escape to close</span>
              <button
                type="button"
                onClick={onClose}
                className="px-3.5 py-1.5 rounded-md bg-brand-primary hover:bg-blue-700 text-white font-medium text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
              >
                Close
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
